import { createContext, FC, useContext, useState } from "react";
import * as _ from "lodash";

import { HeatmapData } from "@ctra/api";

import { getMaxValue, getMinValue } from "../../utils";
import { useChartContext } from "../ChartContext";

type RangeType = { min: number; max: number };

type ContextType = {
  range: RangeType;
  api: {
    updateRange: (data?: HeatmapData["data"]) => RangeType;
    filterByRange: (data: HeatmapData["data"], range: RangeType) => NonNullable<HeatmapData["data"]>;
  };
};

export const defaultRange = { min: Number.MIN_SAFE_INTEGER, max: Number.MAX_SAFE_INTEGER };

const defaultContextValue = {
  range: defaultRange,
  api: {
    updateRange: () => ({ min: 0, max: 0 }),
    filterByRange: () => []
  }
};

const DefaultContext = createContext<ContextType>(defaultContextValue);

/**
 * Range Filter Provider
 * This provides range value context
 * @param param0
 * @returns
 */
const _RangeFilterProvider: FC = ({ children }) => {
  const {
    values: { entities, data: d }
  } = useChartContext<HeatmapData>();

  const chartID = _.get(entities, [0, "id"]);
  const data = _.get(d, [chartID], []);

  const [range, setRange] = useState<RangeType>({
    min: getMinValue(data, "z"),
    max: getMaxValue(data, "z")
  });

  /**
   * Update the range based on the provided data
   * @param {HeatmapData["data"]} data
   */
  const updateRange = (data: HeatmapData["data"] = []) => {
    const range = {
      min: getMinValue(data, "z"),
      max: getMaxValue(data, "z")
    };

    setRange(range);

    return range;
  };

  /**
   * Filter the data by range
   * @param {HeatmapData["data"]} data
   * @param {RangeType} range
   * @returns {{x: string | number, y: string, z: number, context?: PointContext}[]}
   */
  const filterByRange = (data: HeatmapData["data"] = [], range: RangeType) => {
    return _.filter(data, (datum) => datum.z >= range.min && datum.z <= range.max);
  };

  return (
    <DefaultContext.Provider
      value={{
        range,
        api: {
          updateRange,
          filterByRange
        }
      }}
    >
      {children}
    </DefaultContext.Provider>
  );
};

export const RangeFilterContext = {
  Provider: _RangeFilterProvider,
  Consumer: DefaultContext.Consumer
};

/**
 * Hook for the ranger color context
 */
export const useRangeFilter = (): ContextType => useContext(DefaultContext);
